import React, { useCallback, useState } from 'react';
import { ChevronDown, Play } from 'lucide-react';
import { Link } from 'react-router-dom';
import { buttonClasses } from '../../ui/Button';
import AppStoreButtons from '../../ui/AppStoreButtons';
import useKitScene from './useKitScene';
import ScaledStage from './ScaledStage';
import useMediaQuery from '../../../hooks/useMediaQuery';
import VideoDialog, { SPOT_SOURCES, SPOT_TRANSCRIPT } from './VideoDialog';

/**
 * Hero: links Claim, ein Satz, eine Handlung; rechts eine kleine Geschichte
 * aus echten App-Bausteinen — Noah findet Emily (Profilkarte) und fragt sie;
 * Emily nimmt die Anfrage an wie in der App (Chat), die beiden verabreden
 * sich (Session). Sie spielt EINMAL ab (~4 s) und bleibt dann stehen: Dauerbewegung im Randblick lenkt vom
 * Lesen ab (siehe AuraLightBackground). Auf Geraeten mit Maus folgt die
 * Komposition dem Zeiger ganz leicht (Tiefe statt Animation).
 */
const STAGE_W = 640;
const STAGE_H = 540;

const buildHero = (compact) => ({ EO, gsap, root, reduced }) => {
  const ui = EO.ui;
  const M = EO.motion;

  const profile = ui.profileCard({
    name: 'Emily', klasse: '11a', ap: 240, streak: 6, match: 92,
    photo: 'assets/avatars/emily.jpg',
    offers: ['GitHub', 'Python', 'Französisch'], seeks: ['Analysis', 'Klavier'],
    highlight: 'GitHub',
  });
  // Emilys Sicht, wie in der App (Okt 2026): Noahs erste Nachricht kommt als
  // Lernpartnerschafts-Anfrage, Emily nimmt an, Noah tippt und antwortet.
  // Seine Antwort ist eine graue Blase — eine eigene (lila) Blase waere neben
  // Ring, Match-Pille und Vernetzen-Knopf eine Akzentflaeche zu viel. Der lila
  // „Annehmen“-Knopf steht nur bis zum Tipp da und wird dann neutral.
  const chat = ui.chat({
    partner: { name: 'Noah', photo: 'assets/avatars/noah.jpg', status: 'Online' },
    request: { text: 'Hi Emily! Kannst du mir GitHub erklären?' },
    messages: [{ from: 'other', text: 'Danke! Mittwoch in der Bibliothek?', time: '09:42' }],
    typing: true,
    typingSlot: true,
  });
  const req = chat.parts.request.parts;
  const reply = chat.parts.bubbles[0];
  const session = ui.sessionCard({
    status: 'Geplant', type: 'Privat', count: { now: 2, max: 2 },
    title: 'GitHub für Einsteiger',
    place: 'Bibliothek', time: '10:15', date: 'Mi.',
    people: ['assets/avatars/noah.jpg', 'assets/avatars/emily.jpg'],
  });

  // Komposition: links die ganze Profilkarte (inkl. „Möchte lernen“ und
  // „Vernetzen“), rechts daneben Frage und Session — nichts verdeckt die Karte.
  // Breit: feste Buehne (skaliert). Schmal: Karten in App-Originalgroesse
  // untereinander, leicht verschraenkt — verkleinert waere die Schrift unlesbar.
  const layers = compact ? [
    // Schmal: eine mittige Spalte wie der Text darueber — Karten gleich breit,
    // der Chat eine Spur schmaler, leicht uebereinander geschoben.
    { el: profile, css: { width: '100%', maxWidth: '380px', margin: '0 auto' } },
    { el: chat, css: { width: '92%', maxWidth: '350px', margin: '-16px auto 0' } },
    { el: session, css: { width: '100%', maxWidth: '380px', margin: '-16px auto 0' } },
  ] : [
    { el: profile, x: 0, y: 0, depth: 6, width: 340 },
    // Chat mit Anfrage und Antwort ist hoeher als frueher (eine Blase): Er
    // rueckt nach oben, die Session nach unten — sie endet mit der Profilkarte
    // und steht daher ganz rechts daneben, sonst laege sie auf „Vernetzen“.
    { el: chat, x: 318, y: 36, depth: 12, width: 322 },
    { el: session, x: 346, y: 382, depth: 18, width: 294 },
  ];
  // Jede Karte sitzt in einer eigenen Huelle: Die Huelle traegt die Zeiger-
  // Tiefe, die Karte das Einblenden — zwei Bewegungen, die sich nie ins Gehege kommen.
  layers.forEach((layer) => {
    const { el, x, y, width, css } = layer;
    const holder = document.createElement('div');
    if (css) {
      holder.style.position = 'relative';
      Object.assign(holder.style, css);
      el.style.width = '100%';
    } else {
      holder.style.position = 'absolute';
      holder.style.left = `${x}px`;
      holder.style.top = `${y}px`;
      if (width) el.style.width = `${width}px`;
    }
    holder.appendChild(el);
    root.appendChild(holder);
    layer.holder = holder;
  });

  const tl = gsap.timeline({ paused: true, defaults: { overwrite: 'auto' } });
  M.rise(tl, profile, 0, { y: 28, dur: 0.8 });
  M.ring(tl, profile.parts.ring, 0.35, 1.1);
  M.pop(tl, profile.parts.chips, 0.45, { stagger: 0.05, from: 0.7 });
  M.tap(tl, profile.parts.cta, 0.95);
  M.rise(tl, chat, 1.05, { y: 24, dur: 0.7 });
  M.pop(tl, chat.parts.request, 1.3, { from: 0.92 });
  // Annehmen: Tipp, die Knoepfe gehen, „Lernpartnerschaft angenommen“ kommt.
  M.tap(tl, req.accept, 1.85);
  tl.to(req.actions, { opacity: 0, y: -6, duration: 0.2, ease: 'power2.in' }, 2.05);
  tl.fromTo(req.done, { opacity: 0, y: 6 }, { opacity: 1, y: 0, duration: 0.35, ease: EO.ease.emphasis }, 2.2);
  tl.fromTo(chat.parts.typing, { opacity: 0, scale: 0.8, transformOrigin: '0% 100%' },
    { opacity: 1, scale: 1, duration: 0.3, ease: EO.ease.spring }, 2.45);
  // Drei Hopser, dann kommt die Antwort an derselben Stelle — keine Endlosschleife.
  tl.fromTo(chat.parts.typing.children, { y: 0 }, { y: -4, duration: 0.16, ease: 'sine.inOut', yoyo: true, repeat: 5, stagger: 0.08 }, 2.6);
  tl.to(chat.parts.typing, { opacity: 0, duration: 0.12 }, 3.3);
  M.pop(tl, reply, 3.36, { from: 0.85 });
  M.rise(tl, session, 3.2, { y: 24, dur: 0.75 });
  M.pop(tl, session.parts.badges, 3.45, { stagger: 0.06, from: 0.7 });
  M.pop(tl, session.parts.people, 3.65, { stagger: 0.08, from: 0.6 });

  if (reduced) {
    tl.progress(1);
    return undefined;
  }

  // Erst abspielen, wenn Schriften stehen — sonst springen die Kartenhoehen.
  let started = false;
  const start = () => { if (!started) { started = true; tl.play(0); } };
  tl.progress(0).pause();
  if (document.fonts && document.fonts.ready) document.fonts.ready.then(start);
  else start();
  const fallback = setTimeout(start, 600);

  // Zeiger-Tiefe nur mit echter Maus (kein Touch, kein Stift-Wackeln).
  let onMove = null;
  if (!compact && window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
    const movers = layers.map(({ holder, depth }) => ({
      depth,
      x: gsap.quickTo(holder, 'x', { duration: 0.9, ease: 'power3.out' }),
      y: gsap.quickTo(holder, 'y', { duration: 0.9, ease: 'power3.out' }),
    }));
    onMove = (event) => {
      const nx = event.clientX / window.innerWidth - 0.5;
      const ny = event.clientY / window.innerHeight - 0.5;
      movers.forEach((m) => { m.x(-nx * m.depth); m.y(-ny * m.depth); });
    };
    window.addEventListener('pointermove', onMove, { passive: true });
  }

  return () => {
    clearTimeout(fallback);
    if (onMove) window.removeEventListener('pointermove', onMove);
    tl.kill();
  };
};

const HeroSection = ({ onApply }) => {
  const [videoOpen, setVideoOpen] = useState(false);
  const closeVideo = useCallback(() => setVideoOpen(false), []);
  const compact = useMediaQuery('(max-width: 639px)');
  const { ref } = useKitScene(buildHero(compact), [compact]);

  return (
    // Desktop: Der Hero fuellt genau den Bildschirm, „So geht's“ beginnt erst
    // beim Scrollen. Mobil fuellt der Textblock den ersten Bildschirm (die Kante
    // liegt unter den Store-Badges), die Kartenspalte folgt beim Scrollen.
    // Einblenden: reines CSS (index.css, .hero-word/.hero-rise), startet mit dem
    // ersten Bild und wartet nicht aufs Motion-Kit.
    <section aria-labelledby="hero-title" className="relative z-10 lg:min-h-screen lg:flex lg:flex-col">
      <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8 pt-16 lg:pt-24 pb-16 sm:pb-20 lg:pb-24 lg:flex-1 lg:flex lg:items-center">
        <div className="w-full grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
          <div className="relative lg:col-span-6 xl:col-span-6 text-center lg:text-left flex flex-col items-center lg:items-start justify-center py-6 max-lg:min-h-[calc(100vh-4rem)] max-lg:supports-[height:100svh]:min-h-[calc(100svh-4rem)] lg:py-0">
            <p className="hero-rise inline-flex items-center gap-2 text-sm font-medium text-muted-foreground mb-[clamp(1rem,3.5vh,2rem)] lg:mb-6" style={{ '--d': '0ms' }}>
              <span className="w-1.5 h-1.5 rounded-full bg-foreground/40" aria-hidden="true" />
              Peer-Learning für Schulen · im Pilot
            </p>
            <h1 id="hero-title" className="text-center lg:text-left display-heading hero-title text-[clamp(2.4rem,12.4vw,4.5rem)] lg:text-[clamp(3rem,5.4vw,4.9rem)] leading-[1.06]">
              <span className="hero-mask"><span className="hero-word" style={{ '--i': 0 }}>Let&apos;s</span></span>{' '}
              <span className="hero-mask"><span className="hero-word" style={{ '--i': 1 }}>help</span></span><br />
              <span className="whitespace-nowrap">
                <span className="hero-mask"><span className="hero-word" style={{ '--i': 2 }}>each</span></span>{' '}
                <span className="hero-mask"><span className="hero-word" style={{ '--i': 3 }}>otter!</span></span>
              </span>
            </h1>
            <p className="hero-rise mt-[clamp(1.25rem,4vh,2rem)] lg:mt-6 text-lg sm:text-xl leading-relaxed text-foreground/80 max-w-xl" style={{ '--d': '420ms' }}>
              Mit EachOtter finden Schüler:innen an ihrer Schule, wer ihnen weiterhilft –
              und verabreden sich zum Lernen.
            </p>

            <div className="hero-rise self-stretch lg:self-auto mt-[clamp(1.5rem,5vh,2.5rem)] lg:mt-9 flex flex-col sm:flex-row gap-3 justify-center lg:justify-start" style={{ '--d': '520ms' }}>
              <button type="button" onClick={onApply} className={buttonClasses('primary', 'lg')}>
                Als Pilotschule bewerben
              </button>
              <button
                type="button"
                onClick={() => setVideoOpen(true)}
                className={buttonClasses('secondary', 'lg', 'gap-3')}
                aria-haspopup="dialog"
              >
                <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-foreground text-background" aria-hidden="true">
                  <Play className="w-3.5 h-3.5 translate-x-[1px]" fill="currentColor" />
                </span>
                Spot ansehen
                <span className="text-muted-foreground font-normal tabular-nums">57 s</span>
              </button>
            </div>

            <div className="hero-rise mt-[clamp(1.5rem,5.5vh,3rem)] lg:mt-10 flex flex-col sm:flex-row items-center gap-4 justify-center lg:justify-start" style={{ '--d': '620ms' }}>
              <p className="text-sm text-muted-foreground">Die App für Schüler:innen</p>
              <AppStoreButtons variant="compact" className="!justify-center lg:!justify-start" />
            </div>

            {/* Mobil: Pfeil an der unteren Kante des ersten Bildschirms
                (auf niedrigen Bildschirmen nicht — dort braeuchten ihn die Badges). */}
            <span className="lg:hidden [@media(max-height:760px)]:hidden absolute bottom-3 left-1/2 -translate-x-1/2 eo-nudge inline-flex items-center justify-center w-8 h-8 rounded-full border border-border bg-surface/70 text-muted-foreground" aria-hidden="true">
              <ChevronDown className="w-4 h-4" />
            </span>
          </div>

          <div className="lg:col-span-6 xl:col-span-6">
            {compact ? (
              <div ref={ref} className="eo eo-illustration relative mx-auto max-w-[380px] min-h-[860px]" aria-hidden="true" />
            ) : (
              <ScaledStage ref={ref} width={STAGE_W} height={STAGE_H} className="w-full max-w-[640px] mx-auto lg:mr-0" />
            )}
          </div>
        </div>
      </div>

      {/* Hinweis, dass es weitergeht (nur Desktop, dort fuellt der Hero den Bildschirm).
          Wippt beim Laden dreimal und steht dann — keine Endlosbewegung. */}
      <Link
        to={{ pathname: '/', hash: '#so-gehts' }}
        className="hidden lg:flex absolute bottom-6 left-1/2 -translate-x-1/2 flex-col items-center gap-1.5 rounded-lg px-3 py-1 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
      >
        So geht&apos;s
        <span className="eo-nudge inline-flex items-center justify-center w-8 h-8 rounded-full border border-border bg-surface/70" aria-hidden="true">
          <ChevronDown className="w-4 h-4" />
        </span>
      </Link>

      <VideoDialog
        open={videoOpen}
        onClose={closeVideo}
        title="EachOtter in 57 Sekunden"
        sources={SPOT_SOURCES}
        transcript={SPOT_TRANSCRIPT}
      />
    </section>
  );
};

export default HeroSection;
